import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// The waiting list is switched off for now (product decision 2026-10-14, no flow defined yet):
// joinBoothWaitlist / leaveBoothWaitlist stay exported in boothBookingApi and in the Lambda, but the
// page offers no waitlist actions.
import { fetchEventBoothMap, releaseBoothReservation, reserveBooth } from '../../../../../utils/boothBookingApi';
import { HOLD_MINUTES, MAX_HOLDS_PER_COMPANY, boothKeyOf } from '../../../../../utils/boothMap';
import { captureError } from '../../../../../utils/sentryHelpers';
import { hydrateBoothMap } from '../boothMapView';

const POLL_INTERVAL_MS = 20000;

const ACTIONS = {
  reserve: { call: reserveBooth, operation: 'reserve-booth' },
  release: { call: releaseBoothReservation, operation: 'release-booth-reservation' },
};

/**
 * Booth map of an event for the hall-plan booking page (MAP CONTRACT of getEventBoothMap).
 *
 * - polls every 20 s while the browser tab is visible, refreshes when the tab becomes
 *   visible again and right after every action
 * - actions return { success, code, message, color } where message is a translated
 *   text for an inline alert; the same object is exposed as `notice`
 * - clockOffsetMs = server time - local time (from map.serverTime), for hold countdowns
 * - booths are hydrated with their product fields (map.products) right after fetching, see hydrateBoothMap
 * - actions take the booth (identity boothKeyOf(booth)); busyId is the boothKey of the running action
 */
export default function useEventBoothMap({ eventId, companyId, enabled = true }) {
  const { t } = useTranslation();

  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);

  const mountedRef = useRef(true);
  const requestSeqRef = useRef(0);
  const failingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled || !eventId) return null;
    const seq = ++requestSeqRef.current;
    try {
      const data = hydrateBoothMap(await fetchEventBoothMap({ eventId, companyId }));
      // a newer request (e.g. the refresh after an action) wins over an older poll
      if (!mountedRef.current || seq !== requestSeqRef.current) return data;
      setMap(data);
      setError(null);
      failingRef.current = false;
      const serverMs = data && data.serverTime ? Date.parse(data.serverTime) : NaN;
      setClockOffsetMs(Number.isFinite(serverMs) ? serverMs - Date.now() : 0);
      return data;
    } catch (err) {
      // report the first failure of a series only, polling would flood Sentry otherwise
      if (!failingRef.current) {
        captureError(err, {
          feature: 'booth-booking',
          operation: 'fetch-event-booth-map',
          consoleMessage: 'Loading the booth map failed',
          context: { eventId, companyId },
        });
      }
      failingRef.current = true;
      if (mountedRef.current && seq === requestSeqRef.current) setError(err);
      return null;
    } finally {
      if (mountedRef.current && seq === requestSeqRef.current) setLoading(false);
    }
  }, [enabled, eventId, companyId]);

  // initial load + reload on parameter change
  useEffect(() => {
    if (!enabled || !eventId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setMap(null);
    refresh();
  }, [enabled, eventId, companyId, refresh]);

  // polling while visible
  useEffect(() => {
    if (!enabled || !eventId) return undefined;
    const isVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible';
    const interval = setInterval(() => {
      if (isVisible()) refresh();
    }, POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (isVisible()) refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, eventId, refresh]);

  const booths = useMemo(() => (map && Array.isArray(map.booths) ? map.booths : []), [map]);

  const messageForResult = useCallback(
    (result, booth) => {
      const number = (result && result.boothNumber) || (booth && booth.boothNumber) || '';
      switch (result && result.code) {
        case 'HELD':
          return t('Booth {{number}} is held for you for {{minutes}} minutes.', { number, minutes: HOLD_MINUTES });
        case 'RELEASED':
          return t('The hold on booth {{number}} has been released.', { number });
        case 'BOOTH_UNAVAILABLE':
          return t('Booth {{number}} is no longer available.', { number });
        case 'HOLD_LIMIT':
          // the same code is used for the per-company hold cap and for the extension cap of an own hold
          return result && result.status === 'held_by_you'
            ? t('The hold on booth {{number}} cannot be extended further. Release it and reserve it again.', { number })
            : t('Your company can hold at most {{max}} booths at the same time.', { max: MAX_HOLDS_PER_COMPANY });
        case 'FORBIDDEN':
          return t('You are not allowed to book booths for this company.');
        case 'NOT_FOUND':
          return t('The booth could not be found.');
        case 'EVENT_NOT_BOOKABLE':
          return t('Booth booking is not open for this event.');
        default:
          return result && result.success ? t('Done.') : t('The action could not be completed.');
      }
    },
    [t]
  );

  const runAction = useCallback(
    async (name, boothOrKey) => {
      const { call, operation } = ACTIONS[name];
      // the booth object (preferred); a boothKey or a bare eventProductId (single-booth products) is looked up
      const booth =
        boothOrKey && typeof boothOrKey === 'object'
          ? boothOrKey
          : booths.find((b) => boothKeyOf(b) === boothOrKey) || booths.find((b) => b.eventProductId === boothOrKey) || null;
      const eventProductId = booth ? booth.eventProductId : boothOrKey;
      const boothNumber = booth && booth.boothNumber ? booth.boothNumber : undefined;
      const boothKey = booth ? boothKeyOf(booth) : boothOrKey;
      setBusyId(boothKey);
      let outcome;
      try {
        const result = await call({ eventId, eventProductId, companyId, boothNumber });
        outcome = {
          success: !!(result && result.success),
          code: (result && result.code) || null,
          message: messageForResult(result, booth),
          color: result && result.success ? 'success' : 'warning',
          eventProductId,
          boothKey,
        };
      } catch (err) {
        captureError(err, {
          feature: 'booth-booking',
          operation,
          consoleMessage: `Booth action ${name} failed`,
          context: { eventId, eventProductId, boothNumber, companyId },
        });
        outcome = {
          success: false,
          code: 'ERROR',
          message: t('An error occurred. Please try again.'),
          color: 'danger',
          eventProductId,
          boothKey,
        };
      }
      if (mountedRef.current) {
        setNotice(outcome);
        setBusyId(null);
      }
      await refresh();
      return outcome;
    },
    [booths, eventId, companyId, messageForResult, refresh, t]
  );

  const actions = useMemo(
    () => ({
      reserve: (booth) => runAction('reserve', booth),
      release: (booth) => runAction('release', booth),
    }),
    [runAction]
  );

  const clearNotice = useCallback(() => setNotice(null), []);

  return { map, booths, loading, error, refresh, actions, busyId, notice, clearNotice, clockOffsetMs };
}
